import { useState, useEffect } from "react";
import { MessageSquare, Clock, CheckCircle, Send, User, Search, Phone, Mail, Globe, Building2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const statusColors = { new: "bg-red-100 text-red-700", contacted: "bg-blue-100 text-blue-700", negotiating: "bg-yellow-100 text-yellow-700", won: "bg-green-100 text-green-700", lost: "bg-slate-100 text-slate-500" };

export default function InquiryManagement() {
  const { api, user } = useAuth();
  const [inquiries, setInquiries] = useState([]);
  const [filter, setFilter] = useState("all");
  const [selected, setSelected] = useState(null);
  const [reply, setReply] = useState("");
  const [sending, setSending] = useState(false);

  const load = () => { api().get("/inquiries").then(r => setInquiries(r.data)).catch(() => {}); };
  useEffect(load, [api]);

  const filtered = filter === "all" ? inquiries : inquiries.filter(i => i.status === filter);

  const handleReply = async () => {
    if (!reply.trim() || !selected) return;
    setSending(true);
    try {
      const res = await api().post(`/inquiries/${selected.id}/reply`, { message: reply });
      setSelected(res.data);
      setReply("");
      load();
      toast.success("Reply sent!");
    } catch { toast.error("Failed to send reply"); }
    setSending(false);
  };

  const updateStatus = async (id, status) => {
    try {
      const res = await api().put(`/inquiries/${id}`, { status });
      if (selected?.id === id) setSelected(res.data);
      load();
      toast.success("Status updated");
    } catch { toast.error("Failed"); }
  };

  return (
    <div className="space-y-6" data-testid="inquiry-management">
      <div>
        <h2 className="text-2xl font-serif text-navy">CRM / Inquiries</h2>
        <p className="text-slate-400 text-sm font-sans mt-1">{inquiries.length} total inquiries</p>
      </div>

      <div className="flex flex-wrap gap-2">
        {["all", "new", "contacted", "negotiating", "won", "lost"].map(s => (
          <button key={s} onClick={() => setFilter(s)} className={`px-4 py-2 text-sm font-medium font-sans transition-all ${filter === s ? "bg-navy text-white" : "bg-white border border-slate-200 text-slate-600 hover:border-navy"}`} data-testid={`filter-inq-${s}`}>
            {s === "all" ? "All" : s.charAt(0).toUpperCase() + s.slice(1)}
            {s !== "all" && <span className="ml-1.5 text-xs opacity-70">({inquiries.filter(i => i.status === s).length})</span>}
          </button>
        ))}
      </div>

      <div className="grid lg:grid-cols-3 gap-4">
        <div className="lg:col-span-1 space-y-3 max-h-[70vh] overflow-y-auto">
          {filtered.map(inq => (
            <button key={inq.id} onClick={() => setSelected(inq)} className={`w-full text-left p-4 rounded-lg border transition-all ${selected?.id === inq.id ? "border-forest bg-forest/5" : "border-slate-200 bg-white hover:border-forest/50"}`} data-testid={`inq-item-${inq.id}`}>
              <div className="flex items-start justify-between mb-2">
                <h4 className="font-medium text-navy text-sm font-sans">{inq.company_name}</h4>
                <span className={`px-2 py-0.5 text-xs font-medium font-sans ${statusColors[inq.status]}`}>{inq.status}</span>
              </div>
              <p className="text-xs text-slate-500 font-sans">{inq.contact_name} | {inq.country}</p>
              <p className="text-xs text-slate-400 font-sans mt-1 line-clamp-1">{inq.products?.join(", ") || "General inquiry"}</p>
              <p className="text-xs text-slate-300 font-mono mt-2">{new Date(inq.created_at).toLocaleDateString()}</p>
            </button>
          ))}
          {filtered.length === 0 && <p className="text-center py-8 text-slate-400 font-sans">No inquiries found.</p>}
        </div>

        <div className="lg:col-span-2">
          {selected ? (
            <div className="card-dashboard" data-testid="inq-detail">
              <div className="flex items-start justify-between mb-6">
                <div>
                  <h3 className="text-xl font-serif text-navy">{selected.company_name}</h3>
                  <p className="text-slate-400 text-sm font-sans">{selected.contact_name}</p>
                </div>
                <select value={selected.status} onChange={e => updateStatus(selected.id, e.target.value)} className="form-input w-auto text-sm" data-testid="inq-status-select">
                  {["new", "contacted", "negotiating", "won", "lost"].map(s => <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>)}
                </select>
              </div>

              <div className="grid sm:grid-cols-2 gap-4 mb-6">
                {[
                  { icon: Mail, label: "Email", value: selected.email },
                  { icon: Phone, label: "Phone", value: selected.phone },
                  { icon: Globe, label: "Country", value: selected.country },
                  { icon: Building2, label: "Market", value: selected.market_type },
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-3 text-sm">
                    <div className="w-8 h-8 bg-forest/10 rounded flex items-center justify-center"><item.icon className="w-4 h-4 text-forest" /></div>
                    <div><p className="text-xs text-slate-400 font-sans">{item.label}</p><p className="text-slate-700 font-sans">{item.value}</p></div>
                  </div>
                ))}
              </div>

              {selected.products?.length > 0 && (
                <div className="mb-4"><p className="text-sm font-medium text-navy font-sans mb-2">Products Interested:</p><div className="flex flex-wrap gap-1.5">{selected.products.map((p, i) => <span key={i} className="px-2 py-1 bg-forest/10 text-forest text-xs font-sans">{p}</span>)}</div></div>
              )}
              {selected.quantity && <p className="text-sm text-slate-600 font-sans mb-2"><strong>Quantity:</strong> {selected.quantity}</p>}
              {selected.message && <p className="text-sm text-slate-600 font-sans mb-4 p-3 bg-slate-50 rounded">{selected.message}</p>}

              <div className="border-t border-slate-100 pt-4 mt-4">
                <h4 className="text-sm font-medium text-navy font-sans mb-3">Conversation</h4>
                <div className="space-y-3 max-h-60 overflow-y-auto mb-4">
                  {selected.replies?.map((r, i) => (
                    <div key={i} className="flex items-start gap-3">
                      <div className="w-8 h-8 bg-navy rounded-full flex items-center justify-center flex-shrink-0"><User className="w-4 h-4 text-white" /></div>
                      <div className="bg-blue-50 rounded-lg p-3 flex-1">
                        <div className="flex items-center justify-between mb-1"><span className="text-sm font-medium text-navy font-sans">{r.sent_by}</span><span className="text-xs text-slate-400 font-mono">{new Date(r.sent_at).toLocaleString()}</span></div>
                        <p className="text-sm text-slate-600 font-sans">{r.message}</p>
                      </div>
                    </div>
                  ))}
                  {(!selected.replies || selected.replies.length === 0) && <p className="text-sm text-slate-400 font-sans text-center py-4">No replies yet.</p>}
                </div>
                <div className="flex gap-2">
                  <input type="text" value={reply} onChange={e => setReply(e.target.value)} placeholder="Type your reply..." className="form-input flex-1" onKeyDown={e => e.key === "Enter" && handleReply()} data-testid="inq-reply-input" />
                  <button onClick={handleReply} disabled={sending || !reply.trim()} className="px-4 btn-primary flex items-center gap-2" data-testid="inq-reply-btn">
                    <Send className="w-4 h-4" />{sending ? "..." : "Send"}
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <div className="card-dashboard flex items-center justify-center h-96">
              <div className="text-center"><MessageSquare className="w-12 h-12 text-slate-200 mx-auto mb-3" /><p className="text-slate-400 font-sans">Select an inquiry to view details</p></div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
